/**
 * Created by Shikha Gupta on 2021-12-20.
 */

import {WinPresentationController} from "../WinPresentationController";
import {Karen} from "../../Karen";
import {gsap, TimelineLite, TweenLite} from "gsap";
import {KarenSymbol} from "../../symbol/KarenSymbol";
import {IStickyFS} from "../../server/data/IKarenData";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {KarenSymbolState} from "../../symbol/KarenSymbolConfig";
import {KarenParsedGameData} from "../../server/data/KarenParsedGameData";
import {SpineAnimation} from "../../view/SpineAnimation";
import {ScatterAnimation} from "../../view/ScatterAnimation";
import {DisplayObject} from "pixi.js";
import {KarenLayers, Layers} from "../../view/KarenLayers";
import {KarenSoundManager} from "../../sound/KarenSoundManager";
import {KarenSoundConfig} from "../../sound/KarenSoundConfig";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {GameHelper} from "../../../core/utils/GameHelper";
import {KarenStickyXWView} from "../../view/KarenStickyXWView";

export class XWController extends WinPresentationController {
    private _shouldScatterConvert: boolean = false;
    private _mainTL: TimelineLite;
    private _toShake: boolean = false;
    private _shakeDuration: number = 1;
    private _revealedSym: string = "";
    private _highLightDuration: number = 0.25;
    private _stickyXWFSPostions: number[] = [];
    private _isSkipped: boolean = false;
    private _parsedData: KarenParsedGameData = Karen.model.parsedGameData;
    private _bigWinBounce: TweenLite;
    private _stageInitY: number = 0;
    private _toPlayFast: boolean = false;
    private _playFastDuration: number = 0;
    private _xwSpineDurration: number = 0;

    constructor() {
        super("XWController", true);
    }

    public killShakeTween(): void {
        // @ts-ignore
        this._bigWinBounce && this._bigWinBounce.pause() && this._bigWinBounce.kill();
    }

    protected hasWin(): boolean {
        this._parsedData = Karen.model.parsedGameData;
        const scatterPositions: number[] = this._parsedData.scatterPositions!;
        if (this._parsedData.mode === GameMode.FREESPIN) {
            this._stickyXWFSPostions = this._parsedData.stickyFSPositions!;
        } else {
            this._stickyXWFSPostions = [-1, -1, -1, -1, -1 - 1];
        }
        const xWPositions: number[] = this._parsedData.xWPositions!;
        const sCount: number = scatterPositions!.filter((value: number) => {
            return value > -1;
        }).length;
        const stickyXCount: number = this._stickyXWFSPostions!.filter((value: number) => {
            return value > -1;
        }).length;
        this._shouldScatterConvert = sCount < 3 && sCount > 0;
        this._toShake = ( xWPositions[1] > -1 && xWPositions[2] > -1 ) ||
            ( this._shouldScatterConvert && ( scatterPositions![1] > -1 ) && ( scatterPositions![2] > -1 ) ) ||
            ( this._shouldScatterConvert && ( scatterPositions![1] > -1 ) && ( xWPositions![2] > -1 ) ) ||
            ( this._shouldScatterConvert && ( scatterPositions![2] > -1 ) && ( xWPositions![1] > -1 ) );
        this._shakeDuration = this._toShake ? 1 : 0.4;
        return this._shouldScatterConvert || this._parsedData.mysterySymbolReveal !== "" || stickyXCount > 0;
    }

    protected async createWinAnimation(): Promise<void> {
        const tl: TimelineLite = new TimelineLite();
        tl.to(this, {duration: 1});
        await new Promise<void>(resolve => {
            this.scatterToXWayConversion(resolve);
        });
        await Promise.resolve(tl);
    }

    protected scatterToXWayConversion(resolve: (value:any) => void): void {
        this._toPlayFast = Karen.model.parsedGameData.betWins.length == 0 || Karen.apiPlugin.settings.get(APISetting.FAST_SPIN);
        this._isSkipped = false;
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                resolve(undefined);
            }
        });
        const xwayCount: number = this._parsedData.xWPositions!.filter((value: number) => {
            return value > -1;
        }).length;
        let scatterReelId: number = 0;
        const sCount: number = this._parsedData.scatterPositions!.filter((value: number, reelId: number) => {
            if (value > -1) {
                scatterReelId = reelId;
            }
            return value > -1;
        }).length;
        let isInfectious: boolean = false;
        const scatterPositions: number[] = this._parsedData.scatterPositions!;
        const xWPositions: number[] = this._parsedData.xWPositions!;
        const xWInPos: number = this._parsedData.reels[0].indexOf("XW2");
        this._revealedSym = this._parsedData.mysterySymbolReveal;
        const scatterAnim: ScatterAnimation = new ScatterAnimation("scatter");
        let duration: number = this._toPlayFast ? this._playFastDuration : scatterAnim.getAnimDuration("transform_to_xway");
        if (this._shouldScatterConvert) {
            if (GameHelper.isFreeSpin(this._parsedData.mode)) {
                tl.add(scatterAnim.playTurnToBurgerAnimation("turn_to_burger"));
                Karen.stickyXWView.playStickyBurgerNoWinOnReelId(scatterReelId);
            } else {
                tl.add(scatterAnim.playFinalAnimation("transform_to_xway", this._isSkipped, duration), 0);
                if (!this._toPlayFast) {
                    tl.add(() =>{KarenSoundManager.playSoundEffect(KarenSoundConfig.SCATTER_TO_XWAYS)}, 0);
                }
            }

            if (!GameHelper.isFreeSpin(this._parsedData.mode)) {
                scatterPositions.forEach((symIndex: number, reelId: number) => {
                    if (symIndex > -1) {
                        this._revealedSym = this._parsedData.evaluatedArea[reelId][symIndex];
                        tl.add(Karen.reels[reelId].replaceSimpleSymbol(symIndex, "XW", KarenSymbolState.NORMAL, true), 0.01);
                        if (this._toShake) {
                            tl.add(Karen.reels[reelId].getSymbol(symIndex).getShakeAnimation(), duration);
                        }
                    }
                });
            }
        }
        if (this._toShake) {
            tl.add(() => {KarenSoundManager.playSoundEffect(KarenSoundConfig.X_WAYS_SHAKE)}, duration);

        }
        if (this._parsedData.mysterySymbolReveal !== "" && this._toShake) {
            xWPositions.forEach((symIndex: number, reelId: number) => {
                if (symIndex > -1) {
                    tl.add(Karen.reels[reelId].getSymbol(symIndex).getShakeAnimation(), duration);
                }
            });
        }
        if (xWInPos === 1 || xWInPos === 2) {
            this._revealedSym = this._parsedData.evaluatedArea[0][xWInPos - 1];
            isInfectious = true;
        }
        if (Karen.model.parsedGameData.mode === GameMode.FREESPIN) {
            tl.add(this.playPumpingOnPotentialWinInFS(), tl.totalDuration());
        } else {
            tl.add(this.playPumpingOnPotentialWin(), tl.totalDuration());
        }
        const position: number = tl.totalDuration();

        if (isInfectious) {
            let infCount: number = 0;
            let infectiousRevealSound: string = KarenSoundConfig.xWaysInfectiousReveal;
            this._parsedData.xWInfPositions!.forEach((infectedPos: number[], reelId: number) => {
                infectedPos!.forEach((symIndex: number) => {
                    infCount++;
                    if (( xWInPos - 1 === symIndex && reelId === 0 ) || this._parsedData.xWPositions[reelId] === symIndex || this._parsedData.scatterPositions![reelId] === symIndex) {
                        tl.add(this.playInfectiousAnimation(reelId, symIndex, "reveal"), position);
                    }
                });
            });
            if (this._shouldScatterConvert && sCount > 0) {
                infectiousRevealSound = KarenSoundConfig.xWaysInfectiousRevealWild;
            }
            if (xwayCount > 0) {
                infectiousRevealSound = KarenSoundConfig.xWaysInfectiousRevealWild;
            }
            tl.add(() => {KarenSoundManager.playSoundEffect(infectiousRevealSound)}, position);
            tl.add(this.playInfAnimaion());
            this._stickyXWFSPostions.forEach((symIndex: number, reelId: number) => {
                if (symIndex > -1) {
                    tl.add(() => {
                        Karen.stickyXWView.hideStickyXW(reelId, symIndex);
                    }, position);
                }
            });
        } else {
            let firstMultiplierReel = -1;
            let xcount = 0;
            let scount = 0;
            let isFSTrigger: boolean = GameHelper.isFreeSpinTrigger(this._parsedData.mode, this._parsedData.nextMode);
            let isFSMode: boolean = GameHelper.isFreeSpin(this._parsedData.mode);
            if (!( isFSMode )) {
                xWPositions.forEach((symIndex: number, reelId: number) => {
                    if (symIndex > -1) {
                        xcount++;
                        if (xcount == 1) {
                            firstMultiplierReel = reelId;
                        }
                        this._revealedSym = this._parsedData.evaluatedArea[reelId][symIndex];
                        tl.add(this.playXWAnimation(reelId, symIndex, "reveal", firstMultiplierReel), position);
                    }
                });
                if (!isFSTrigger) {
                    scatterPositions!.forEach((symIndex: number, reelId: number) => {
                        if (symIndex > -1) {
                            scount++;
                            if (scount == 1 && ( xwayCount == 0 )) {
                                firstMultiplierReel = reelId;
                            }
                            this._revealedSym = this._parsedData.evaluatedArea[reelId][symIndex];
                            tl.add(this.playXWAnimation(reelId, symIndex, "reveal", firstMultiplierReel), position);
                        }
                    });
                }
            } else {
                let stickyXWCount = 0;
                let playStickyXWRevealS: boolean = false;
                this._stickyXWFSPostions.forEach((symIndex: number, reelId: number) => {
                    if (symIndex > -1) {
                        stickyXWCount++;
                        playStickyXWRevealS = stickyXWCount === 1;
                        this._revealedSym = this._parsedData.evaluatedArea[reelId][symIndex];
                        tl.add(this.stickyXWAnimation(reelId, symIndex, playStickyXWRevealS), position);

                    }
                });
                tl.add(this.fsModeStickyMultipliers());
            }
        }
        this._mainTL = tl;
    }

    protected skip(): void {
        this._isSkipped = true;
        if (this._mainTL && this._mainTL.isActive()) {
            this._mainTL.progress(1);
            this.skipScatterAnimations();
            this.killShakeTween();
        }
    }

    private playXWAnimation(reelId: number, symIndex: number, animName: string, firstMultiplierReel?: number): TimelineLite {
        const totalWinRatio = Karen.model.parsedGameData.totalWin / Karen.model.parsedGameData.playedBetValue;
        const tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                if (this._isSkipped) {
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.X_WAYS_REVEAL);
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.SYM_SHAKE);
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.X_WAYS_SHAKE);
                }
                if (this._isSkipped && ( firstMultiplierReel === reelId )) {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.X_WAYS_REVEAL_FAST);
                }
            }
        });
        let name: string = "x_way";
        const XW: SpineAnimation = new SpineAnimation(name);
        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symIndex);
        const stagePosition: PIXI.Point = symbol!.getStagePosition();
        XW.setStagePosition(stagePosition);

        this._xwSpineDurration = this._toPlayFast ? this._playFastDuration : XW!.getAnimDuration(animName);
        let revealPos: string = this._toPlayFast ? this._playFastDuration.toString() : "-=0.7";
        tl.add(XW.playSpineAnimation(animName, this._isSkipped, this._xwSpineDurration));
        tl.add(Karen.reels[reelId].replaceSimpleSymbol(symIndex, this._revealedSym), revealPos);
        //to replace symbol before smoke starts
        tl.to(this, {duration: 0.01});
        if (( firstMultiplierReel === reelId )) {
            if (totalWinRatio <= 9) {
                tl.add(() => {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                }, tl.totalDuration());
            }
            tl.add(() => {
                if (!this._isSkipped) {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.X_WAYS_REVEAL)
                } else {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.X_WAYS_REVEAL_FAST);
                }
            }, 0);
        }
        if (totalWinRatio <= 9) {
            tl.add(this.revealXWInfMultiplier(reelId, symIndex, false));
            tl.add(() => {
                Karen.xwaysMultiplierView.setXWays(this._parsedData.xWayNumbersOnEachNudgeStep.toString());
            });
        }else{
            tl.add(this.XWWinMultipliers(reelId,symIndex));
        }
        return tl;
    }

    private playInfectiousAnimation(reelId: number, symIndex: number, animName: string): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let name: string = reelId === 0 ? "xWayinfectious_0" : "xWayinfectious";
        const InfXW: SpineAnimation = new SpineAnimation(name);
        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symIndex);
        const stagePosition: PIXI.Point = symbol!.getStagePosition();
        InfXW.setStagePosition(stagePosition);
        this._xwSpineDurration = this._toPlayFast ? this._playFastDuration : InfXW!.getAnimDuration(animName);
        let revealPos: string = this._toPlayFast ? this._playFastDuration.toString() : "-=0.7";
        tl.add(InfXW.playSpineAnimation(animName, this._isSkipped, this._xwSpineDurration));
        tl.add(Karen.reels[reelId].replaceSimpleSymbol(symIndex, this._revealedSym), revealPos);
        //to replace symbol before smoke starts
        tl.to(this, {duration: 0.01});
        return tl;
    }

    private stickyXWAnimation(reelId: number, symIndex: number, toPlaySound: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._xwSpineDurration = this._toPlayFast ? this._playFastDuration : Karen.stickyXWView.fetchDuration("reveal");
        let revealPos: string = this._toPlayFast ? this._playFastDuration.toString() : "-=0.7";
        tl.add(Karen.stickyXWView.playRevealAnimation(reelId));
        if (toPlaySound) {
            tl.add(() => {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.X_WAYS_STICKY_REVEAL);
            }, 0);
        }
        tl.add(Karen.reels[reelId].replaceSimpleSymbol(symIndex, this._revealedSym), revealPos);
        tl.to(this, {duration: 0.01});
        return tl;
    }

    private playInfAnimaion(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._parsedData.xWInfPositions!.forEach((infectedPos: number[], reelId: number) => {
            infectedPos!.forEach((symIndex: number) => {
                const XW: SpineAnimation = new SpineAnimation("infectious_frame");
                let duration: number = this._toPlayFast ? this._playFastDuration : XW!.getAnimDuration("anticipation_big");
                const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symIndex);
                const stagePosition: PIXI.Point = symbol!.getStagePosition();
                XW.setStagePosition(stagePosition);
                if (reelId === 0) {
                    tl.add(XW.playSpineAnimation("anticipation_big", this._isSkipped, duration), 0.01);
                } else {
                    tl.add(XW.playSpineAnimation("anticipation_small", this._isSkipped, duration), 0.01);
                }
            });
        });
        tl.add(() => {KarenSoundManager.playSoundEffect(KarenSoundConfig.xWaysInfectiousSelection)}, 0.01);
        const position: number = tl.totalDuration();
        let offSet: number = 0.1;
        let count: number = 0;
        if (this._parsedData.betWins.length == 0) {
            offSet = 0;
        }
        if (this._parsedData.currentWinRatioConfig!.bigWinLevel > 0) {
            offSet = 0.5;
        }
        this._parsedData.xWInfPositions!.forEach((infectedPos: number[], reelId: number) => {
            count++;
            infectedPos!.forEach((symIndex: number) => {
                tl.add(
                    this.revealXWInfMultiplier(reelId, symIndex, true)
                    , position + ( count * offSet ));
            });
            if (infectedPos.length) {
                tl.add(() => {
                    KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                }, ( position + ( count * offSet ) ) + 0.1);
            }
        });
        // #154 - Reset final ways size
        tl.add(() => {
            Karen.xwaysMultiplierView.setXWays(Karen.model.parsedGameData.xWayNumbersOnEachNudgeStep.toString());
        }, position + ( ( count + 1 ) * offSet ));
        return tl;
    }

    /*#204*/
    private fsModeStickyMultipliers(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const position: number = 0;
        let offSet: number = 0.6;
        let count: number = 0;
        let lastReelId: number = 0;
        if (!Karen.model.parsedGameData.isStickyFSParticipatingWin) {
            offSet = 0;
        }
        let valueToBeUpdated: number = 0;
        let multPos: number = 0;
        this._stickyXWFSPostions!.forEach((symIndex: number, reelId: number) => {
            const multiplierVal: number = ( this._parsedData.symbolSizes[reelId][symIndex] );
            if (symIndex > -1) {
                if (Karen.model.parsedGameData.isStickyFSParticipatingWin) {
                    if (this._parsedData.symbolWinCombinaionEachReel[reelId] > this._parsedData.symbolWinCombinaionEachReel[lastReelId]) {
                        count++;
                        valueToBeUpdated = this._parsedData.symbolWinCombinaionEachReel[reelId];
                    }
                    offSet -= ( offSet ) * 0.1;
                    multPos = position + ( count * offSet );
                    tl.add(
                        Karen.xwaysMultiplierView.update(valueToBeUpdated)
                        , multPos);
                }
                tl.add(
                        () => {
                            Karen.reels[reelId].getSymbol(symIndex).revealQuickMultiplier(multiplierVal, this._isSkipped);
                            KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                        }
                    , multPos);
                lastReelId = reelId;
            }
            if (GameHelper.isFreeSpin(Karen.model.parsedGameData.mode)) {
                tl.add(() => {
                }, tl.totalDuration() + 0.2);
            }
        });
        // #154 - Reset final ways size
        tl.add(() => {
            Karen.xwaysMultiplierView.setXWays(Karen.model.parsedGameData.xWayNumbersOnEachNudgeStep.toString());
        }, position + ( ( count + 1 ) * offSet ));
        return tl;
    }

    private XWWinMultipliers(reelId:number,symIndex:number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const position: number = 0;
        let offSet: number = 0.6;
        let count: number = 0;
        let multPos: number = 0;
            let multiplierVal: number = ( this._parsedData.symbolSizes[reelId][symIndex] );
            let isFsTrigger: boolean = false;
            if (GameHelper.isFreeSpinTrigger(Karen.model.parsedGameData.mode, Karen.model.parsedGameData.nextMode)) {
                isFsTrigger = true;
            }
            if (this._parsedData.xWPositions[reelId] > -1 || ( !isFsTrigger && this._parsedData.scatterPositions![reelId] > -1 )) {
                offSet -= ( offSet ) * 0.1;
                count++;
                if(!this._parsedData.XWParticipatingInWin[reelId]){
                    offSet=0;
                }
                multPos = position + ( count * offSet );
                let symId = ( symIndex > -1 ) ? symIndex : this._parsedData.scatterPositions![reelId];
                multiplierVal = ( symIndex > -1 ) ? this._parsedData.symbolSizes[reelId][symIndex] : this._parsedData.symbolSizes![reelId][symId];
                tl.add(() => {
                            Karen.reels[reelId].getSymbol(symId).revealMultiplier(multiplierVal,false, this._isSkipped);
                            KarenSoundManager.playSoundEffect(KarenSoundConfig.MULTIPLIER_STAMP.next());
                        }, multPos);
            }
        // #154 - Reset final ways size
        tl.add(() => {
            Karen.xwaysMultiplierView.setXWays(Karen.model.parsedGameData.xWayNumbersOnEachNudgeStep.toString());
        });
        return tl;
    }

    private revealXWInfMultiplier(reelId: number, symIndex: number, isInfected: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const wildIndex: number = Karen.model.reels[5].indexOf("W", 1);
        const isWildNudge: boolean = wildIndex > -1 && wildIndex < Karen.model.parsedGameData.evaluatedArea![5].length;
        tl.add(() => {
            const multiplierVal: number = ( this._parsedData.symbolSizes[reelId][symIndex] );
            if (reelId === 5 && this._parsedData.xWInfPositions[5].length > 0) {
                for (let symId: number = 0; symId < this._parsedData.xWInfPositions[5].length; symId++) {
                    if (this._parsedData.xWInfPositions[reelId][symId] === symIndex && isWildNudge) {
                        Karen.reels[reelId].getSymbol(symIndex).revealXWMultiplier(multiplierVal, isInfected, this._isSkipped, true);
                    } else if (this._parsedData.xWInfPositions[reelId][symId] === symIndex) {
                        Karen.reels[reelId].getSymbol(symIndex).revealMultiplier(multiplierVal, isInfected, this._isSkipped, false);
                    }
                }
            } else {
                Karen.reels[reelId].getSymbol(symIndex).revealMultiplier(multiplierVal, isInfected, this._isSkipped);
            }
            Karen.xwaysMultiplierView.setXWays(this._parsedData.finalXWayNumbersOnEachReel[reelId].toString());
            if (this._parsedData.currentWinRatioConfig!.bigWinLevel > 0) {
                isInfected && this.shakeStage();
            }
        }, 0.1);
        tl.to(this, {}, 0.2);
        return tl;
    }

    private playPumpingOnPotentialWin(): TimelineLite {
        let tween: TimelineLite = new TimelineLite();
        let toPlayShakeSound: boolean = false;
        this._parsedData.highlightPotentialWin.forEach((eachReel: boolean[], reelId: number) => {
            eachReel.forEach((toHighLight: boolean, symId: number) => {
                if (toHighLight) {
                    toPlayShakeSound = true;
                    const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symId);
                    tween.add(Karen.reels[reelId].getSymbol(symId).getShakeAnimation(), this._highLightDuration);
                }
            })
        });
        if (toPlayShakeSound) {
            tween.add(() => {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SYM_SHAKE);
            }, this._highLightDuration);
        }
        return tween;
    }

    private playPumpingOnPotentialWinInFS(): TimelineLite {
        let tween: TimelineLite = new TimelineLite({
            onComplete: () => {
                if (this._isSkipped) {
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.SYM_SHAKE);
                    KarenSoundManager.stopSoundEffect(KarenSoundConfig.X_WAYS_SHAKE);
                }
            }
        });
        let toPlayShakeSound: boolean = false;
        this._parsedData.highlightPotentialWin1.forEach((eachReel: boolean[], reelId: number) => {
            eachReel.forEach((toHighLight: boolean, symId: number) => {
                if (this._stickyXWFSPostions[reelId] !== symId) {
                    if (toHighLight) {
                        toPlayShakeSound = true;
                        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symId);
                        tween.add(Karen.reels[reelId].getSymbol(symId).getShakeAnimation(), this._highLightDuration);
                    }
                }
            })
        });
        if (toPlayShakeSound) {
            tween.add(() => {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SYM_SHAKE);
            }, this._highLightDuration);
            tween.add(() => {
                KarenSoundManager.playSoundEffect(KarenSoundConfig.SYM_SHAKE);
            }, 2.5 * this._highLightDuration);
        }
        this._parsedData.highlightPotentialWin2.forEach((eachReel: boolean[], reelId: number) => {
            eachReel.forEach((toHighLight: boolean, symId: number) => {
                if (this._stickyXWFSPostions[reelId] !== symId) {
                    if (toHighLight) {
                        const symbol: KarenSymbol = Karen.reels[reelId].getSymbol(symId);
                        tween.add(Karen.reels[reelId].getSymbol(symId).getShakeAnimation(), 2.5 * this._highLightDuration);
                    }
                }
            })
        });
        return tween;
    }

    private skipScatterAnimations(): void {
        let scatterEl: PIXI.spine.Spine[] | undefined = <PIXI.spine.Spine[] | undefined>(KarenLayers.getLayer(Layers.SCATTER_OVERLAY)?.children);
        scatterEl?.forEach((scatterAnimation: PIXI.spine.Spine) => {
            let trackEntry: PIXI.spine.core.TrackEntry = scatterAnimation.state.getCurrent(0);
            if (trackEntry) {
                trackEntry.trackTime = trackEntry.animation.duration;
            }
        });
    }

    private shakeStage(): void {
        if (this._isSkipped) {
            this._bigWinBounce && this._bigWinBounce.kill();
            return
        }
        this._stageInitY = KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent.y;
        if (this._bigWinBounce && this._bigWinBounce.isActive() && this._bigWinBounce.totalProgress() > 0 && this._bigWinBounce.totalProgress() < 1) {
            this._bigWinBounce.progress(1);
        }
        this._bigWinBounce = TweenLite.to(KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent, 0.2, {
            y: 10,
            x: 10,
            ease: Karen.StageShakeBounce,
            onComplete: () => {
                KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent.y = 0;
                KarenLayers.getLayer(Layers.LOW_SYMBOLS)!.parent.x = 0;
            }
        });
    }
}
